import { useUserActiveOrganization } from "@/api";
import { TypographyMuted } from "@/components/typography/Muted";
import { TypographySM } from "@/components/typography/SM";
import { Button } from "@/components/ui/button";
import { CopyButton } from "@/components/ui/copyButton";
import Dialog from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Link } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { Dispatch, SetStateAction } from "react";
import { PartialApp } from "../../types/app.type";
import APIKeys from "./APIKeys";
import AddAPIKey from "./AddAPIKey";

interface Props {
  app: PartialApp;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

function AppAPI({ app, setOpen }: Props) {
  const { activeOrganization } = useUserActiveOrganization();

  return (
    <Dialog.Root open onOpenChange={setOpen}>
      <Dialog.Content className="max-w-3xl w-full max-h-[90vh]">
        <Dialog.Header>
          <Dialog.Title>API settings</Dialog.Title>
          <Dialog.Description>
            Manage the API for{" "}
            <span className="font-semibold">{app.displayName}</span>
          </Dialog.Description>
        </Dialog.Header>
        <div className="w-full grid grid-cols-[16rem_1fr] items-center gap-y-6 mt-5 justify-items-start">
          <TypographySM>API URL</TypographySM>
          <div className="grid grid-cols-[1fr_36px] gap-2 md:gap-4 items-center">
            <div className="h-9 w-full px-3 py-1 shadow-sm min-w-36 flex flex-row items-center text-sm bg-brand-gray-50/60 dark:bg-brand-gray-800/60 border border-brand-gray-200 dark:border-brand-gray-700 rounded-md">
              <TypographyMuted className="truncate">
                <span className="text-brand-gray-700 dark:text-brand-gray-200">
                  {app.defaultDeployment?.apiURL || ""}
                </span>
              </TypographyMuted>
            </div>

            <CopyButton
              copyText={app.defaultDeployment?.apiURL || ""}
              variant="ghost"
            />
          </div>
          {app.defaultDeployment?.apiDocsPath &&
            activeOrganization &&
            app.slug && (
              <>
                <TypographySM>View the app API docs</TypographySM>
                <Link
                  to="/organization/$organizationSlug/private/$appSlug"
                  params={{
                    organizationSlug: activeOrganization.slug,
                    appSlug: app.slug
                  }}
                  search={{ appPath: app.defaultDeployment.apiDocsPath }}
                >
                  <Button variant="default">
                    Open docs <ExternalLink size={14} className="ml-2" />
                  </Button>
                </Link>
              </>
            )}
        </div>

        <Separator className="my-2" />

        <div className="flex justify-between items-center">
          <TypographySM>Manage API keys</TypographySM>
          <AddAPIKey app={app} />
        </div>
        <APIKeys app={app} />

        <Dialog.Footer className="mt-8">
          <Dialog.Close asChild>
            <Button type="button" variant="secondary">
              Close
            </Button>
          </Dialog.Close>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export default AppAPI;
